/**
 * Traducción de errores de la API de Flotas a la vista (ADR 0021).
 *
 * `apiClient` ya normaliza el error uniforme del backend a `ApiError`. Acá se decide qué
 * mostrar y a qué campo del formulario apuntar:
 * - Siempre se muestra el `message` del backend (fuente de verdad del texto).
 * - Si el error identifica el campo (`details.field` / `details.fieldName`), se marca ese.
 * - Si no, se cae al campo conocido por el `code` estable. Los códigos de flota siguen la
 *   convención `FLEET_*`; los de alta inline reutilizan los `CUSTOMER_*` de clientes.
 */
import { ApiError } from '@/lib/http/apiClient';

export interface FleetApiError {
  /** Mensaje del backend, listo para mostrar. */
  message: string;
  /** Nombre del campo del formulario a marcar, si el error lo identifica. */
  field?: string;
}

/**
 * Códigos estables del backend → campo del formulario a marcar. No se parsean mensajes
 * (ADR 0021); si aparece un código nuevo se muestra el mensaje sin marcar campo.
 *
 * La lista se alinea con los códigos reales de
 * `services/backend/src/shared/errors/app-error.ts`:
 * - `FLEET_*` de la flota (ADR 0047).
 * - `CUSTOMER_*` del alta inline de conductor (mismo backend de clientes).
 * Solo se mapean los códigos que identifican un campo; los de entidad, validación
 * general y sync (`FLEET_VEHICLE_SYNC_FAILED`) muestran el mensaje del backend tal cual.
 */
const CODE_FIELD: Record<string, string> = {
  // Flota.
  FLEET_CODIGO_DUPLICATED: 'codigoFlota',
  // Conductores.
  FLEET_DRIVER_ALREADY_LINKED: 'customerId',
  // Vehículos.
  FLEET_VEHICLE_RFID_DUPLICATED: 'rfidTag',
  FLEET_VEHICLE_PLATE_DUPLICATED: 'plate',
  // Alta inline de conductor: reutiliza los códigos del backend de clientes.
  CUSTOMER_EMAIL_DUPLICATED: 'email',
  CUSTOMER_PHONE_DUPLICATED: 'phone',
  CUSTOMER_DOCUMENT_DUPLICATED: 'documentNumber',
  CUSTOMER_DOCUMENT_INVALID: 'documentNumber',
};

/** Extrae el campo de `details` (convención ADR 0021) si viene. */
function fieldFromDetails(details: unknown): string | undefined {
  if (typeof details !== 'object' || details === null) {
    return undefined;
  }
  const record = details as Record<string, unknown>;
  for (const key of ['field', 'fieldName'] as const) {
    const value = record[key];
    if (typeof value === 'string' && value !== '') {
      return value;
    }
  }
  return undefined;
}

/**
 * Describe un error de flota para mostrarlo en la página. `fallbackMessage` ajusta el
 * texto genérico según la acción (alta, edición, vínculo, vehículo, etc.).
 */
export function describeFleetApiError(
  error: unknown,
  fallbackMessage = 'No se pudo guardar la flota.',
): FleetApiError {
  if (error instanceof ApiError) {
    return {
      message: error.message,
      field: fieldFromDetails(error.details) ?? CODE_FIELD[error.code],
    };
  }
  if (error instanceof Error && error.message) {
    return { message: error.message };
  }
  return { message: fallbackMessage };
}
